import React, { useState } from 'react';

import { useLocation, useParams } from 'react-router';

import { Calendar } from 'wanderu-component-datepicker';
import WanderuErrorBoundary from 'wanderu-component-error-boundary';
import { Redirect } from 'wanderu-component-react-router-link';
import {
    Lightbox,
    Modal,
    WDSButton,
} from 'wanderu-components';
import type { SearchParams } from 'wanderu-components-search';
import { localize } from 'wanderu-i18n';
import {
    buildUrlParams,
    buildValidUrl,
    formatDateForSearchUrl,
} from 'wanderu-utils';

import Events from './events/ReturnButtonEvents';
import {
    useArriveTripSelector,
    useSearchParamsSelector,
} from './hooks';

import styles from './ReturnButton.scss';

import type { TFunction } from 'i18next';
import type { MomentInput, Moment } from 'moment';
import type { match as Match } from 'react-router';

interface ReturnButtonProps {
    i18nMoment: (date?: MomentInput) => Moment;
    t: TFunction;
}

interface SearchUrlProps extends SearchParams {
    arriveDate: string;
    outboundTripId: string;
}

function buildSearchUrl(props: SearchUrlProps, returnDate: string): string | undefined {
    const {
        arriveDate,
        destinationPlaceId,
        destinationText,
        originPlaceId,
        originText,
        outboundTripId,
    } = props;

    const params = {
        departDate: formatDateForSearchUrl(arriveDate),
        departTripId: outboundTripId,
        destinationPlaceId,
        destinationText,
        direction: 'return',
        originPlaceId,
        originText,
        returnDate: formatDateForSearchUrl(returnDate),
    };

    const urlParams = buildUrlParams(params);
    return buildValidUrl(urlParams);
}

export const ReturnButton: React.FC<ReturnButtonProps> = ({
    i18nMoment,
    t,
}) => {
    const params: Match['params'] = useParams();
    const location = useLocation();

    const {
        outboundTripId,
        returnTripId,
    } = params;

    const searchParams = useSearchParamsSelector({ location, match: { params } });

    const outboundArrive = useArriveTripSelector(outboundTripId);
    const {
        localizedDateTime: arriveDate = '',
    } = outboundArrive;

    const [show, setShow] = useState(false);
    const [redirect, setRedirect] = useState(false);
    const [date, setDate] = useState(i18nMoment(arriveDate));

    if (returnTripId) {
        return null;
    }
    const onDateChange = (e: { target: { value: Moment } }) => {
        const dateSelected = e.target.value;

        Events.addReturnDateSelected({
            dateSelected,
            tripId: outboundTripId,
        });

        setRedirect(true);
        setDate(dateSelected);
    };

    const toggleCalendar = () => {
        Events.addReturnTripDropdownClicked({
            tripId: outboundTripId,
        });

        setShow(!show);
    };

    if (redirect) {
        return (
            <Redirect
                href={buildSearchUrl(
                    {
                        ...searchParams,
                        arriveDate,
                        outboundTripId,
                    },
                    date?.format()
                )}
                push
            />
        );
    }

    return (
        <>
            <WDSButton
                className={styles.button}
                onClick={toggleCalendar}
                variant="unstyled"
            >
                {t('add-return-trip')}
            </WDSButton>

            {show && (
                <Modal>
                    <Lightbox onHide={toggleCalendar} showCloseButton={false}>
                        <WanderuErrorBoundary
                            fallback=""
                            scope="ReturnButton Calendar"
                        >
                            <Calendar
                                calendarClass={styles.calendarContainer}
                                dayHeaderClass={styles.dayHeaderClass}
                                minDate={date}
                                onDateChange={onDateChange}
                                selectedDate={date}
                            />
                        </WanderuErrorBoundary>
                    </Lightbox>
                </Modal>
            )}
        </>
    );
};

export default localize(ReturnButton, ['t', 'i18nMoment'], 'baseApp');
